import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { api } from '../context/AuthContext';

const inp = { width: '100%', padding: '11px 14px', borderRadius: '10px', boxSizing: 'border-box', border: '1px solid #e2e8f0', fontSize: '14px', fontFamily: "'Sora',sans-serif", outline: 'none', color: '#1e293b', background: '#fff' };
const lbl = { display: 'block', fontSize: '12px', fontWeight: '600', color: '#64748b', marginBottom: '6px', marginTop: '14px' };

export default function CustomerRegister() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [linkInfo, setLinkInfo] = useState(null);
  const [linkError, setLinkError] = useState('');
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otp, setOtp] = useState('');
  const [devOtp, setDevOtp] = useState('');
  const [pincodeErr, setPincodeErr] = useState('');

  const [form, setForm] = useState({
    full_name: '', age: '', phone: '', email: '', current_address: '', current_pincode: '',
    permanent_address: '', occupation_type: 'EMPLOYEE',
    occupation_details: { company: '', designation: '', salary_range: '', college: '', course: '', year: '', business_name: '', business_type: '', description: '' },
    aadhaar_number: '', pan_number: '', gstin: '', password: '', confirm_password: '',
  });

  const [kycImages, setKycImages] = useState({
    aadhaar_front: null, aadhaar_back: null, pan_card: null,
  });
  const [kycPreviews, setKycPreviews] = useState({
    aadhaar_front: null, aadhaar_back: null, pan_card: null,
  });

  const handleImageChange = (field) => (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error('Image must be under 5MB');
    setKycImages(prev => ({ ...prev, [field]: file }));
    const reader = new FileReader();
    reader.onload = (ev) => setKycPreviews(prev => ({ ...prev, [field]: ev.target.result }));
    reader.readAsDataURL(file);
  };

  const [fieldErrors, setFieldErrors] = React.useState({});

  const validateField = (k, v) => {
    let err = '';
    if (k === 'full_name') {
      if (!v) err = 'Full name is required';
      else if (!/^[a-zA-Z\s]+$/.test(v)) err = 'Name must contain letters only, no numbers';
      else if (v.length < 2) err = 'Name must be at least 2 characters';
    }
    if (k === 'age') {
      if (!v) err = 'Age is required';
      else if (isNaN(v) || parseInt(v) < 18) err = 'Age must be 18 or above';
      else if (parseInt(v) > 100) err = 'Please enter a valid age';
    }
    if (k === 'current_pincode') {
      if (!v) err = 'Pincode is required';
      else if (!/^\d{6}$/.test(v)) err = 'Pincode must be exactly 6 digits';
    }
    if (k === 'aadhaar_number') {
      if (!v) err = 'Aadhaar is required';
      else if (!/^\d{12}$/.test(v)) err = 'Aadhaar must be exactly 12 digits, numbers only';
    }
    if (k === 'pan_number') {
      if (v && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(v.toUpperCase())) err = 'PAN format: 5 letters + 4 digits + 1 letter (e.g. ABCDE1234F)';
    }
    if (k === 'password') {
      if (!v) err = 'Password is required';
      else if (v.length < 6) err = 'Password must be at least 6 characters';
    }
    if (k === 'email') {
      if (v && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) err = 'Please enter a valid email address';
    }
    return err;
  };

  const set = k => e => {
    const v = e.target.value;
    setForm(f => ({ ...f, [k]: v }));
    const err = validateField(k, v);
    setFieldErrors(prev => ({ ...prev, [k]: err }));
  };

  const errStyle = { fontSize: '11px', color: '#dc2626', marginTop: '3px', display: 'block' };
  const okStyle = { fontSize: '11px', color: '#16a34a', marginTop: '3px', display: 'block' };
  const getInp = (k) => ({ ...inp, borderColor: fieldErrors[k] ? '#dc2626' : (form[k] && !fieldErrors[k] ? '#16a34a' : '#e2e8f0'), borderWidth: '1.5px' });
  const setOcc = k => e => setForm(f => ({ ...f, occupation_details: { ...f.occupation_details, [k]: e.target.value } }));

  useEffect(() => {
    api.get(`/auth/register/validate/${token}`)
      .then(r => setLinkInfo(r.data.link_info))
      .catch(err => setLinkError(err.response?.data?.message || 'Invalid link'));
  }, [token]);

  useEffect(() => {
    if (form.current_pincode && linkInfo) {
      if (form.current_pincode.length === 6 && form.current_pincode !== linkInfo.pincode)
        setPincodeErr(`Must match lender's pincode: ${linkInfo.pincode}`);
      else setPincodeErr('');
    }
  }, [form.current_pincode, linkInfo]);

  const [phoneExists, setPhoneExists] = useState(false);

  const sendOTP = async () => {
    if (!form.phone || form.phone.length < 10) return toast.error('Enter valid 10-digit phone');
    setPhoneExists(false);
    try {
      // Check if phone already registered
      const check = await api.post('/auth/otp/send', {
        phone: form.phone,
        email: form.email || undefined,
        purpose: 'CUSTOMER_REGISTER'
      });
      setOtpSent(true);
      setDevOtp(null);
      if (form.email) {
        toast.success(`OTP sent to ${form.email} ✉️`, { duration: 6000 });
      } else {
        toast.success('OTP sent! Check your email.', { duration: 6000 });
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message;
      if (err.response?.status === 409 || msg.toLowerCase().includes('already registered')) {
        setPhoneExists(true);
        toast.error('This phone number is already registered.');
      } else {
        toast.error('OTP failed: ' + msg);
      }
    }
  };

  const resetPhone = () => {
    setPhoneExists(false);
    setOtpSent(false);
    setOtpVerified(false);
    setOtp('');
    setForm(f => ({ ...f, phone: '' }));
  };

  const verifyOTP = async () => {
    try {
      await api.post('/auth/otp/verify', { phone: form.phone, otp, purpose: 'CUSTOMER_REGISTER' });
      setOtpVerified(true); toast.success('Phone verified! ✓');
    } catch { toast.error('Invalid OTP. Try resending.'); }
  };

  const submit = async () => {
    if (!otpVerified) return toast.error('Verify phone first');
    if (form.password !== form.confirm_password) return toast.error('Passwords do not match');
    if (form.password.length < 6) return toast.error('Password must be at least 6 characters');
    if (pincodeErr) return toast.error(pincodeErr);
    if (!/^[6-9]\d{9}$/.test(form.phone)) return toast.error('Invalid phone. Must be 10 digits starting with 6-9.');
    if (!/^\d{12}$/.test(form.aadhaar_number)) return toast.error('Aadhaar must be exactly 12 digits.');
    if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test((form.pan_number || '').toUpperCase())) return toast.error('Invalid PAN format. Must be like ABCDE1234F.');
    if (!/^\d{6}$/.test(form.current_pincode)) return toast.error('Pincode must be exactly 6 digits.');
    if (form.gstin && form.gstin.length !== 15) return toast.error('GSTIN must be 15 characters');
    setLoading(true);
    try {
      const formData = new FormData();
      Object.entries({ ...form, age: parseInt(form.age) }).forEach(([k, v]) =>
        formData.append(k, typeof v === 'object' ? JSON.stringify(v) : v)
      );
      formData.append('aadhaar_front', kycImages.aadhaar_front);
      formData.append('aadhaar_back', kycImages.aadhaar_back);
      formData.append('pan_card', kycImages.pan_card);

      const r = await api.post(`/auth/register/customer/${token}`, formData);
      toast.success(r.data.previous_customer ? `Welcome back! Trust score: ${r.data.trust_score}` : 'Registered! Awaiting lender approval.');
      navigate('/login');
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setLoading(false); }
  };

  const page = { minHeight: '100vh', background: 'linear-gradient(135deg,#0c1a2e,#0f172a)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', fontFamily: "'Sora',sans-serif", padding: '2rem 1rem' };
  const card = { background: '#fff', borderRadius: '20px', padding: '2rem', width: '100%', maxWidth: '520px', boxShadow: '0 20px 60px rgba(0,0,0,0.4)' };

  if (linkError) return (
    <div style={page}><div style={{ ...card, textAlign: 'center', padding: '3rem' }}>
      <div style={{ fontSize: '48px', marginBottom: '12px' }}>🔗</div>
      <h2 style={{ color: '#dc2626' }}>Invalid Link</h2>
      <p style={{ color: '#64748b' }}>{linkError}</p>
    </div></div>
  );

  if (!linkInfo) return <div style={page}><div style={{ ...card, textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>Checking link...</div></div>;

  return (
    <div style={page}>
      <div style={card}>
        <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '48px', height: '48px', borderRadius: '12px', background: 'linear-gradient(135deg,#06b6d4,#3b82f6)', fontSize: '20px', marginBottom: '8px' }}>₹</div>
          <h1 style={{ fontSize: '18px', fontWeight: '700', color: '#0f172a', margin: 0 }}>Customer Registration</h1>
        </div>

        {/* Lender banner */}
        <div style={{ background: '#eff6ff', borderRadius: '12px', padding: '10px 14px', marginBottom: '1.5rem', border: '1px solid #bfdbfe' }}>
          <div style={{ fontSize: '12px', color: '#1d4ed8', fontWeight: '700' }}>🏪 {linkInfo.shop_name}</div>
          <div style={{ fontSize: '11px', color: '#3b82f6' }}>Lender: {linkInfo.admin_name} · {linkInfo.city} · PIN: {linkInfo.pincode}</div>
          <div style={{ fontSize: '11px', color: '#f97316', marginTop: '2px', fontWeight: '600' }}>⚠️ Your pincode must be: {linkInfo.pincode}</div>
        </div>

        {/* Steps */}
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1.5rem' }}>
          {['Personal', 'Occupation', 'Documents'].map((s, i) => (
            <React.Fragment key={i}>
              <div style={{ flex: 1, textAlign: 'center' }}>
                <div style={{ width: '26px', height: '26px', borderRadius: '50%', background: step > i + 1 ? '#16a34a' : step === i + 1 ? '#3b82f6' : '#e2e8f0', color: step >= i + 1 ? '#fff' : '#94a3b8', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: '700' }}>{step > i + 1 ? '✓' : i + 1}</div>
                <div style={{ fontSize: '9px', color: step === i + 1 ? '#3b82f6' : '#94a3b8', marginTop: '2px', fontWeight: '600' }}>{s}</div>
              </div>
              {i < 2 && <div style={{ height: '1px', flex: 0.5, background: step > i + 1 ? '#16a34a' : '#e2e8f0', marginBottom: '12px' }} />}
            </React.Fragment>
          ))}
        </div>

        {/* STEP 1: Personal */}
        {step === 1 && (
          <div>
            <label style={lbl}>Full Name *</label>
            <input style={getInp('full_name')} placeholder="Enter your full name (letters only)" value={form.full_name}
              onChange={e => { const v = e.target.value.replace(/[^a-zA-Z\s]/g,''); setForm(f=>({...f,full_name:v})); setFieldErrors(p=>({...p,full_name:validateField('full_name',v)})); }} />
            {fieldErrors.full_name && <span style={errStyle}>⚠ {fieldErrors.full_name}</span>}
            {form.full_name && !fieldErrors.full_name && <span style={okStyle}>✓ Valid</span>}

            <label style={lbl}>Age *</label>
            <input style={getInp('age')} type="number" min={18} max={100} placeholder="Age (must be 18+)" value={form.age} onChange={set('age')} />
            {fieldErrors.age && <span style={errStyle}>⚠ {fieldErrors.age}</span>}
            {form.age && !fieldErrors.age && <span style={okStyle}>✓ Valid age</span>}

            <label style={lbl}>Current Address *</label>
            <textarea style={{ ...inp, height: '65px', resize: 'none' }} placeholder="Full current address" value={form.current_address} onChange={set('current_address')} />

            <label style={lbl}>Current Pincode *</label>
            <input style={{...inp, borderColor: pincodeErr ? '#dc2626' : fieldErrors.current_pincode ? '#dc2626' : form.current_pincode && form.current_pincode.length===6 && !pincodeErr ? '#16a34a' : '#e2e8f0', borderWidth:'1.5px'}}
              placeholder={`Must be ${linkInfo.pincode} (lender's area)`} maxLength={6} value={form.current_pincode}
              onChange={e => { const v = e.target.value.replace(/[^0-9]/g,''); setForm(f=>({...f,current_pincode:v})); setFieldErrors(p=>({...p,current_pincode:validateField('current_pincode',v)})); }} />
            {(fieldErrors.current_pincode || pincodeErr) && <span style={errStyle}>⚠ {pincodeErr || fieldErrors.current_pincode}</span>}
            {form.current_pincode && form.current_pincode.length===6 && !pincodeErr && !fieldErrors.current_pincode && <span style={okStyle}>✓ Pincode matches lender area</span>}
            {pincodeErr && <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '3px' }}>❌ {pincodeErr}</div>}
            {form.current_pincode === linkInfo.pincode && form.current_pincode.length === 6 && (
              <div style={{ color: '#16a34a', fontSize: '12px', marginTop: '3px' }}>✅ Pincode matches!</div>
            )}

            <label style={lbl}>Permanent Address (optional)</label>
            <textarea style={{ ...inp, height: '55px', resize: 'none' }} placeholder="If different from above" value={form.permanent_address} onChange={set('permanent_address')} />

            <button onClick={() => {
              if (!form.full_name || !form.age || !form.current_address || !form.current_pincode) return toast.error('Fill all required fields');
              if (pincodeErr) return toast.error(pincodeErr);
              setStep(2);
            }} style={{ marginTop: '1.5rem', width: '100%', padding: '12px', background: 'linear-gradient(135deg,#3b82f6,#06b6d4)', color: '#fff', border: 'none', borderRadius: '12px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', fontFamily: "'Sora',sans-serif" }}>
              Continue →
            </button>
          </div>
        )}

        {/* STEP 2: Occupation */}
        {step === 2 && (
          <div>
            <label style={lbl}>Occupation *</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '1rem' }}>
              {['STUDENT', 'EMPLOYEE', 'BUSINESS', 'OTHER'].map(o => (
                <button key={o} onClick={() => setForm(f => ({ ...f, occupation_type: o }))}
                  style={{ padding: '10px', borderRadius: '10px', cursor: 'pointer', background: form.occupation_type === o ? '#eff6ff' : '#f8fafc', color: form.occupation_type === o ? '#1d4ed8' : '#64748b', fontWeight: '700', fontSize: '12px', fontFamily: "'Sora',sans-serif", border: form.occupation_type === o ? '2px solid #3b82f6' : '2px solid transparent' }}>
                  {o === 'STUDENT' ? '🎓 Student' : o === 'EMPLOYEE' ? '💼 Employee' : o === 'BUSINESS' ? '🏪 Business' : '👤 Other'}
                </button>
              ))}
            </div>

            {form.occupation_type === 'EMPLOYEE' && <>
              <label style={lbl}>Company Name *</label><input style={inp} placeholder="Where you work" value={form.occupation_details.company} onChange={setOcc('company')} />
              <label style={lbl}>Designation</label><input style={inp} placeholder="Job title" value={form.occupation_details.designation} onChange={setOcc('designation')} />
              <label style={lbl}>Salary Range</label>
              <select style={inp} value={form.occupation_details.salary_range} onChange={setOcc('salary_range')}>
                <option value="">Select</option>
                {['Below ₹15,000', '₹15,000–30,000', '₹30,000–60,000', '₹60,000–1,00,000', 'Above ₹1,00,000'].map(r => <option key={r}>{r}</option>)}
              </select>
            </>}

            {form.occupation_type === 'STUDENT' && <>
              <label style={lbl}>College/School *</label><input style={inp} placeholder="Institution name" value={form.occupation_details.college} onChange={setOcc('college')} />
              <label style={lbl}>Course</label><input style={inp} placeholder="e.g. B.Tech" value={form.occupation_details.course} onChange={setOcc('course')} />
              <label style={lbl}>Year</label><input style={inp} placeholder="e.g. 2nd Year" value={form.occupation_details.year} onChange={setOcc('year')} />
            </>}

            {form.occupation_type === 'BUSINESS' && <>
              <label style={lbl}>Business Name *</label><input style={inp} placeholder="Business name" value={form.occupation_details.business_name} onChange={setOcc('business_name')} />
              <label style={lbl}>Business Type</label><input style={inp} placeholder="e.g. Retail" value={form.occupation_details.business_type} onChange={setOcc('business_type')} />
            </>}

            {form.occupation_type === 'OTHER' && <>
              <label style={lbl}>Description</label>
              <textarea style={{ ...inp, height: '65px', resize: 'none' }} placeholder="Describe your work" value={form.occupation_details.description} onChange={setOcc('description')} />
            </>}

            <div style={{ display: 'flex', gap: '8px', marginTop: '1.5rem' }}>
              <button onClick={() => setStep(1)} style={{ flex: 1, padding: '12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: "'Sora',sans-serif", color: '#374151' }}>← Back</button>
              <button onClick={() => setStep(3)} style={{ flex: 2, padding: '12px', background: 'linear-gradient(135deg,#3b82f6,#06b6d4)', color: '#fff', border: 'none', borderRadius: '12px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', fontFamily: "'Sora',sans-serif" }}>Continue →</button>
            </div>
          </div>
        )}

        {/* STEP 3: Documents + Phone */}
        {step === 3 && (
          <div>
            {/* KYC Notice */}
            <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '10px', padding: '10px 14px', marginBottom: '1rem' }}>
              <div style={{ fontSize: '12px', fontWeight: '700', color: '#92400e' }}>📋 KYC Required</div>
              <div style={{ fontSize: '11px', color: '#78350f', marginTop: '2px' }}>Aadhaar & PAN are mandatory. Upload clear photos of your documents.</div>
            </div>

            <label style={lbl}>Aadhaar Number * <span style={{ color: '#dc2626' }}>(Mandatory)</span></label>
            <input style={getInp('aadhaar_number')} placeholder="12-digit Aadhaar number (numbers only)" maxLength={12} value={form.aadhaar_number}
              onChange={e => { const v = e.target.value.replace(/[^0-9]/g,''); setForm(f=>({...f,aadhaar_number:v})); setFieldErrors(p=>({...p,aadhaar_number:validateField('aadhaar_number',v)})); }} />
            {fieldErrors.aadhaar_number && <span style={errStyle}>⚠ {fieldErrors.aadhaar_number}</span>}
            {form.aadhaar_number && !fieldErrors.aadhaar_number && form.aadhaar_number.length===12 && <span style={okStyle}>✓ Valid Aadhaar</span>}
            {form.aadhaar_number.length > 0 && form.aadhaar_number.length !== 12 && <div style={{ fontSize: '11px', color: '#dc2626', marginTop: '3px' }}>Must be exactly 12 digits</div>}
            {form.aadhaar_number.length === 12 && <div style={{ fontSize: '11px', color: '#16a34a', marginTop: '3px' }}>✓ Valid Aadhaar format</div>}

            {/* Aadhaar Images */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', margin: '10px 0' }}>
              {[{ field: 'aadhaar_front', label: 'Aadhaar Front *' }, { field: 'aadhaar_back', label: 'Aadhaar Back *' }].map(({ field, label }) => (
                <div key={field}>
                  <label style={{ ...lbl, marginTop: '6px' }}>{label}</label>
                  <label style={{ display: 'block', cursor: 'pointer' }}>
                    <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageChange(field)} />
                    {kycPreviews[field] ? (
                      <div style={{ position: 'relative' }}>
                        <img src={kycPreviews[field]} alt={label} style={{ width: '100%', height: '90px', objectFit: 'cover', borderRadius: '8px', border: '2px solid #16a34a' }} />
                        <div style={{ position: 'absolute', bottom: '4px', right: '4px', background: '#16a34a', borderRadius: '50%', width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px' }}>✓</div>
                      </div>
                    ) : (
                      <div style={{ height: '90px', border: '2px dashed #cbd5e1', borderRadius: '8px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', gap: '4px' }}>
                        <span style={{ fontSize: '22px' }}>📷</span>
                        <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600' }}>Upload Photo</span>
                      </div>
                    )}
                  </label>
                </div>
              ))}
            </div>

            <label style={lbl}>PAN Number * <span style={{ color: '#dc2626' }}>(Mandatory)</span></label>
            <input style={{...getInp('pan_number'), textTransform:'uppercase'}} placeholder="e.g. ABCDE1234F" maxLength={10} value={form.pan_number}
              onChange={e => { const v = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g,''); setForm(f=>({...f,pan_number:v})); setFieldErrors(p=>({...p,pan_number:validateField('pan_number',v)})); }} />
            {fieldErrors.pan_number && <span style={errStyle}>⚠ {fieldErrors.pan_number}</span>}
            {form.pan_number && !fieldErrors.pan_number && form.pan_number.length===10 && <span style={okStyle}>✓ Valid PAN</span>}
            {form.pan_number.length > 0 && form.pan_number.length !== 10 && <div style={{ fontSize: '11px', color: '#dc2626', marginTop: '3px' }}>Must be exactly 10 characters</div>}
            {form.pan_number.length === 10 && <div style={{ fontSize: '11px', color: '#16a34a', marginTop: '3px' }}>✓ Valid PAN format</div>}

            {/* PAN Image */}
            <label style={lbl}>PAN Card Photo *</label>
            <label style={{ display: 'block', cursor: 'pointer' }}>
              <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageChange('pan_card')} />
              {kycPreviews.pan_card ? (
                <div style={{ position: 'relative' }}>
                  <img src={kycPreviews.pan_card} alt="PAN" style={{ width: '100%', height: '90px', objectFit: 'cover', borderRadius: '8px', border: '2px solid #16a34a' }} />
                  <div style={{ position: 'absolute', bottom: '4px', right: '4px', background: '#16a34a', borderRadius: '50%', width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px' }}>✓</div>
                </div>
              ) : (
                <div style={{ height: '70px', border: '2px dashed #cbd5e1', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', gap: '8px' }}>
                  <span style={{ fontSize: '22px' }}>📷</span>
                  <span style={{ fontSize: '12px', color: '#94a3b8', fontWeight: '600' }}>Upload PAN Card Photo</span>
                </div>
              )}
            </label>

            <label style={lbl}>GSTIN (optional)</label>
            <input
              style={{ ...inp, textTransform: 'uppercase' }}
              placeholder="15-digit GST number (if applicable)"
              maxLength={15}
              value={form.gstin}
              onChange={e => setForm(f => ({ ...f, gstin: e.target.value.toUpperCase() }))}
            />
            {form.gstin && form.gstin.length > 0 && form.gstin.length !== 15 && (
              <div style={{ fontSize: '11px', color: '#f97316', marginTop: '3px' }}>GSTIN must be 15 characters</div>
            )}
            {form.gstin && form.gstin.length === 15 && (
              <div style={{ fontSize: '11px', color: '#16a34a', marginTop: '3px' }}>✓ Valid GSTIN format</div>
            )}

            <label style={lbl}>Password *</label>
            <input style={getInp('password')} type="password" placeholder="Min 6 characters" value={form.password} onChange={set('password')} />
            {fieldErrors.password && <span style={errStyle}>⚠ {fieldErrors.password}</span>}
            {form.password && !fieldErrors.password && <span style={okStyle}>✓ Valid password</span>}

            <label style={lbl}>Confirm Password *</label>
            <input style={{...inp, borderColor: form.confirm_password && form.confirm_password!==form.password ? '#dc2626' : form.confirm_password && form.confirm_password===form.password ? '#16a34a' : '#e2e8f0', borderWidth:'1.5px'}} type="password" placeholder="Repeat password" value={form.confirm_password} onChange={set('confirm_password')} />
            {form.confirm_password && form.confirm_password !== form.password && <span style={errStyle}>⚠ Passwords do not match</span>}
            {form.confirm_password && form.confirm_password === form.password && <span style={okStyle}>✓ Passwords match</span>}

            <label style={lbl}>Email Address (OTP bhi aayega yahan)</label>
            <input style={getInp('email')} type="email" placeholder="customer@email.com (optional)" value={form.email} onChange={set('email')} />
            {fieldErrors.email && <span style={errStyle}>⚠ {fieldErrors.email}</span>}
            {form.email && !fieldErrors.email && <span style={okStyle}>✓ Valid email</span>}

            {/* Phone OTP */}
            <label style={lbl}>Phone Number *</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input style={{ ...inp, flex: 1, borderColor: fieldErrors.phone ? '#dc2626' : form.phone && !fieldErrors.phone ? '#16a34a' : '#e2e8f0', borderWidth:'1.5px' }}
                type="tel" placeholder="10-digit mobile (e.g. 9876543210)" maxLength={10} value={form.phone}
                onChange={e => { const v = e.target.value.replace(/[^0-9]/g,''); setForm(f=>({...f,phone:v})); setFieldErrors(p=>({...p,phone:validateField('phone',v)})); }}
                disabled={otpVerified} />
              {!otpVerified && (
                <button onClick={sendOTP} style={{ padding: '11px 14px', borderRadius: '10px', background: '#3b82f6', color: '#fff', border: 'none', fontWeight: '600', cursor: 'pointer', fontFamily: "'Sora',sans-serif", fontSize: '13px', whiteSpace: 'nowrap' }}>
                  {otpSent ? 'Resend' : 'Send OTP'}
                </button>
              )}
            </div>

            {phoneExists && (
              <div style={{ marginTop: '8px', padding: '12px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: '#dc2626' }}>⚠️ Phone number already registered</div>
                  <div style={{ fontSize: '11px', color: '#ef4444', marginTop: '2px' }}>Please use a different number</div>
                </div>
                <button onClick={resetPhone} style={{ padding: '7px 14px', borderRadius: '8px', background: '#dc2626', color: '#fff', border: 'none', fontWeight: '600', cursor: 'pointer', fontFamily: "'Sora',sans-serif", fontSize: '12px', whiteSpace: 'nowrap' }}>
                  ✏️ Edit Number
                </button>
              </div>
            )}

            {otpSent && !otpVerified && (
              <div>
                <div style={{ marginTop: '10px', padding: '10px 14px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px' }}>
                  <div style={{ fontSize: '12px', color: '#15803d', fontWeight: '600' }}>✉️ OTP sent to your email!</div>
                  <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>Check your inbox and enter the 6-digit OTP below.</div>
                </div>
                <label style={lbl}>Enter OTP *</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input style={{ ...inp, flex: 1, letterSpacing: '0.3em', fontSize: '20px', textAlign: 'center' }} placeholder="• • • • • •" maxLength={6} value={otp} onChange={e => setOtp(e.target.value)} />
                  <button onClick={verifyOTP} style={{ padding: '11px 14px', borderRadius: '10px', background: '#16a34a', color: '#fff', border: 'none', fontWeight: '600', cursor: 'pointer', fontFamily: "'Sora',sans-serif", fontSize: '13px' }}>Verify</button>
                </div>
              </div>
            )}

            {otpVerified && (
              <div style={{ marginTop: '8px', padding: '10px 14px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                <span>✅</span><div style={{ fontWeight: '700', color: '#15803d', fontSize: '13px' }}>Phone {form.phone} verified!</div>
              </div>
            )}

            <div style={{ display: 'flex', gap: '8px', marginTop: '1.5rem' }}>
              <button onClick={() => setStep(2)} style={{ flex: 1, padding: '12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: "'Sora',sans-serif", color: '#374151' }}>← Back</button>
              <button onClick={submit} disabled={!otpVerified || loading}
                style={{ flex: 2, padding: '12px', background: otpVerified && !loading ? 'linear-gradient(135deg,#3b82f6,#06b6d4)' : '#e2e8f0', color: otpVerified ? '#fff' : '#94a3b8', border: 'none', borderRadius: '12px', fontSize: '14px', fontWeight: '700', cursor: otpVerified ? 'pointer' : 'not-allowed', fontFamily: "'Sora',sans-serif" }}>
                {loading ? 'Submitting...' : 'Complete Registration ✓'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
